// AppManager.tsx - Updated with CreateAppView support and localStorage with 30-minute cache

import React, { useState, useEffect } from 'react';
import { 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { 
  ref,
  uploadBytes,
  getDownloadURL,
  getStorage
} from 'firebase/storage';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../hooks/AuthProvider';
import AppsPage from './AppsPage/AppsPage';

// Permission constants
const DEPLOYMENT_ADMIN_PERMISSION = 'DEPLOYMENT_ADMIN';

// LocalStorage keys
const SELECTED_ORG_KEY = 'dineplatform_selected_organization';
const LOCATION_CONFIG_CACHE_KEY = 'dineplatform_location_config_cache';
const LOCATION_CONFIG_TIMESTAMP_KEY = 'dineplatform_location_config_timestamp';

// Cache duration: 30 minutes in milliseconds
const CACHE_DURATION = 30 * 60 * 1000;

// Organization interface based on your payload structure
interface Organization {
  id: string;
  orgId: string;
  orgName: string;
  country: string;
  countryCode: string;
  currencyCode: string;
  region: string;
  availableTimezones: string[];
  currencySymbol: string;
  stripeAccount: string;
  _metadata: {
    createdAt: {
      _seconds: number;
      _nanoseconds: number;
    };
    updatedAt: {
      _seconds: number;
      _nanoseconds: number;
    };
  };
}

// Define interfaces for App data
interface Theme {
  backgroundColor: string;
  textColor: string;
  cardColor: string;
  topbarBackgroundColor: string;
  topbarIconColor: string;
  topbarBorderBottomColor: string;
  sidebarBackgroundColor: string;
  sidebarIconColor: string;
  sidebarIconColorSelected: string;
  sidebarTextColor: string;
  sidebarBorderColor: string;
  borderColor: string;
  categoryIndicatorColor: string;
  cartBackgroundColor: string;
  cartCardColor: string;
  cartTextColor: string;
  cartButtonColor: string;
  cartButtonTextColor: string;
  cartSummaryBorder: string;
  payButtonColor: string;
  payButtonTextColor: string;
  loaderColor: string;
  searchBarColor?: string;
  menuButtons?: boolean;
}

interface ThemeConfig {
  variant: 'modern-light' | 'modern-dark';
  accentColor: string;
  custom?: Theme;
}

interface DomainConfig {
  connected: boolean;
}

interface App {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  icon: string;
  url: string;
  requiredPermission: string;
  region: string;
  type: string;
  status?: string;
  deploymentApproved?: boolean;
  initializationComplete?: boolean;
  adminSetupComplete?: boolean;
  firebaseProjectId?: string;
  firebaseConfig?: FirebaseConfig;
  locationId?: string;
  organizationId: string;
  theme?: ThemeConfig;
  themeConfigured?: boolean;
  domainConfigured?: boolean;
  iconConfigured?: boolean;
  appIcon?: string;
  domains?: Record<string, DomainConfig>;
}

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

interface MetadataField {
  id: string;
  name: string;
  enabled?: boolean;
  organizationId?: string;
}

interface ThemeUpdateRequest {
  id: string;
  appId: string;
  appName: string;
  requestedBy: string;
  requestedAt: Date | any;
  themeData: Theme;
  status: 'pending' | 'approved' | 'rejected';
}

interface AppManagerProps {
  db: Firestore;
  storage: ReturnType<typeof getStorage>;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

export type { Theme, ThemeConfig, App, FirebaseConfig, MetadataField, ThemeUpdateRequest, DomainConfig, Organization };

// Utility functions for cache management
let inMemoryCache: { data: Record<string, Organization> | null; timestamp: number } = { data: null, timestamp: 0 };

const getCachedLocationConfig = (): Record<string, Organization> | null => {
  try {
    // Check in-memory cache first (for same session)
    if (inMemoryCache.data && (Date.now() - inMemoryCache.timestamp < CACHE_DURATION)) {
      console.log('Using in-memory cache');
      return inMemoryCache.data;
    }

    // Check localStorage cache
    const cachedData = localStorage.getItem(LOCATION_CONFIG_CACHE_KEY);
    const timestamp = localStorage.getItem(LOCATION_CONFIG_TIMESTAMP_KEY);
    
    if (!cachedData || !timestamp) {
      return null;
    }
    
    const cacheTime = parseInt(timestamp, 10);
    const now = Date.now();
    
    // Check if cache is still valid (within 30 minutes)
    if (now - cacheTime > CACHE_DURATION) {
      // Cache expired, remove it
      localStorage.removeItem(LOCATION_CONFIG_CACHE_KEY);
      localStorage.removeItem(LOCATION_CONFIG_TIMESTAMP_KEY);
      inMemoryCache = { data: null, timestamp: 0 };
      return null;
    }
    
    const parsedData = JSON.parse(cachedData);
    // Update in-memory cache
    inMemoryCache = { data: parsedData, timestamp: cacheTime };
    
    return parsedData;
  } catch (error) {
    console.error('Error reading location config cache:', error);
    // Clear corrupted cache
    localStorage.removeItem(LOCATION_CONFIG_CACHE_KEY);
    localStorage.removeItem(LOCATION_CONFIG_TIMESTAMP_KEY);
    inMemoryCache = { data: null, timestamp: 0 };
    return null;
  }
};

const setCachedLocationConfig = (data: Record<string, Organization>): void => {
  try {
    const timestamp = Date.now();
    localStorage.setItem(LOCATION_CONFIG_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(LOCATION_CONFIG_TIMESTAMP_KEY, timestamp.toString());
    // Update in-memory cache
    inMemoryCache = { data, timestamp };
  } catch (error) {
    console.error('Error caching location config:', error);
  }
};

const AppManager: React.FC<AppManagerProps> = ({ 
  db, 
  storage,
  functions, 
  onError, 
  onSuccess, 
  setLoading 
}) => {
  const { currentUser, hasPermission, userPermissions } = useAuth();
  
  // Organization state
  const [organizations, setOrganizations] = useState<Record<string, Organization>>({});
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>("");
  const [organizationsLoading, setOrganizationsLoading] = useState(true);

  // Cloud function reference
  const getLocationConfig = httpsCallable(functions, 'getLocationConfig');

  // Load organizations with caching
  const loadOrganizations = async (forceRefresh: boolean = false) => {
    if (!currentUser?.email) {
      setOrganizationsLoading(false);
      return;
    }

    setOrganizationsLoading(true);
    setLoading(true);

    try {
      // Check cache first unless force refresh is requested
      if (!forceRefresh) {
        const cachedData = getCachedLocationConfig();
        if (cachedData) {
          console.log(`Loaded ${Object.keys(cachedData).length} organizations from cache`);
          setOrganizations(cachedData);
          setOrganizationsLoading(false);
          setLoading(false);
          return;
        }
      }

      // Fetch from server
      console.log('Fetching location config from server...');
      const result = await getLocationConfig({});
      
      // @ts-ignore - We know the structure from the cloud function
      if (result.data && result.data.success) {
        // @ts-ignore
        const orgData = result.data.data;
        
        // Cache the fresh data
        setCachedLocationConfig(orgData);
        
        setOrganizations(orgData);
        console.log(`Loaded ${Object.keys(orgData).length} organizations from server and cached`);
      } else {
        onError('Failed to load organizations.');
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      onError('Failed to load organizations. Please try again.');
    } finally {
      setOrganizationsLoading(false);
      setLoading(false);
    }
  };

  // Initialize organization from localStorage when organizations are loaded
  useEffect(() => {
    // Only run this when organizations are first loaded and we don't have an organizationId yet
    if (Object.keys(organizations).length > 0 && !selectedOrganizationId) {
      const savedOrgId = localStorage.getItem(SELECTED_ORG_KEY);
      
      if (savedOrgId && organizations[savedOrgId]) {
        console.log('Loading saved organization from localStorage:', savedOrgId);
        setSelectedOrganizationId(savedOrgId);
      } else {
        // If no saved org or saved org doesn't exist, pick the first available one
        const firstOrgId = Object.keys(organizations)[0];
        if (firstOrgId) {
          console.log('No saved organization found, selecting first available:', firstOrgId);
          setSelectedOrganizationId(firstOrgId);
        }
      }
    }
  }, [organizations, selectedOrganizationId]);

  // Load organizations when component mounts or user changes
  useEffect(() => {
    if (currentUser?.email) {
      loadOrganizations();
    } else {
      setOrganizationsLoading(false);
      setLoading(false);
    }
  }, [currentUser?.email]);

  // Handle organization selection
  const handleOrganizationChange = (orgId: string) => {
    console.log('Organization changed to:', orgId);
    setSelectedOrganizationId(orgId);
  };

  // Function to refresh organizations (for external use)
  const refreshOrganizations = () => {
    loadOrganizations(true);
  };

  // Show loading state while organizations are loading
  if (organizationsLoading) {
    return (
      <div style={{ 
        height: 'calc(100vh - 114px)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#0a0a0a',
        color: '#e5e7eb'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #374151', 
            borderTop: '3px solid #4338ca',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p>Loading organizations...</p>
        </div>
      </div>
    );
  }

  // Show authentication required state
  if (!currentUser?.email) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 114px)',
        flexDirection: 'column',
        gap: '16px',
        padding: '20px',
        backgroundColor: '#0a0a0a',
        color: '#e5e7eb'
      }}>
        <h2 style={{ color: '#f9fafb', margin: 0, fontWeight: 600 }}>Authentication Required</h2>
        <p style={{ color: '#9ca3af', margin: 0, textAlign: 'center' }}>Please log in to view your apps.</p>
      </div>
    );
  }

  // Show error if no organizations available
  if (Object.keys(organizations).length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 114px)',
        flexDirection: 'column',
        gap: '16px',
        padding: '20px',
        backgroundColor: '#0a0a0a',
        color: '#e5e7eb'
      }}>
        <h2 style={{ color: '#f9fafb', margin: 0, fontWeight: 600 }}>No Organizations Available</h2>
        <p style={{ color: '#9ca3af', margin: 0, textAlign: 'center' }}>
          No organizations found. Please contact support.
        </p>
        <button 
          onClick={() => refreshOrganizations()}
          style={{
            padding: '8px 16px',
            backgroundColor: '#4338ca',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <AppsPage 
      db={db}
      functions={functions}
      organizationId={selectedOrganizationId}
      setOrganizationId={setSelectedOrganizationId}
      organizations={organizations}
      onOrganizationChange={handleOrganizationChange}
      onError={onError}
      onSuccess={onSuccess}
      setLoading={setLoading}
    />
  );
};

export default AppManager;